import { Component } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Global Error Boundary — évite l'écran blanc total quand un composant crashe.
 * Utilisé au niveau racine dans App.js.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log utile en dev/preview
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
    // Force route reset — utile quand la page en cours est bugguée
    try {
      window.location.href = "/";
    } catch (_) {
      /* ignore */
    }
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
          <div
            data-testid="error-boundary-fallback"
            className="paper p-8 max-w-lg w-full text-center space-y-4"
          >
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h2 className="font-heading text-2xl mb-2">Une erreur est survenue</h2>
              <p className="text-sm text-muted-foreground">
                Le composant précédent a rencontré un problème. Rechargez ou revenez à l'accueil pour continuer.
              </p>
              {this.state.error?.message && (
                <p className="text-[11px] text-muted-foreground/70 mt-3 font-mono break-words">
                  {String(this.state.error.message).slice(0, 200)}
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-center pt-2">
              <Button
                data-testid="error-reload"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Recharger la page
              </Button>
              <Button data-testid="error-home" className="btn-primary" onClick={this.reset}>
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
